import { useEffect, useRef, useState } from 'react';
import { supabase } from '../auth/supabaseClient';
import { useHistory, useLocation } from 'react-router-dom';

import { safeReturnTo } from '../auth/returnTo';

const Login = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnTo = safeReturnTo(params.get('returnTo'));
  // Intent belongs to this explicit signup route, never a referrer or stored flag.
  // A normal login returning to /raffle is not raffle signup consent.
  const raffleSignup = params.get('mode') === 'signup' &&
    params.get('signupSource') === 'raffle' && returnTo === '/raffle';
  const [mode, setMode] = useState(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const history = useHistory();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSending(true);
    setMessage('');
    setError('');

    if (mode === 'signup') {
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?returnTo=${encodeURIComponent(returnTo)}`,
          // Persist with the account so email confirmation on another browser (or
          // a later sign-in) does not depend on local storage or callback params.
          data: raffleSignup ? { signup_intent: 'raffle-website-v1' } : {},
        },
      });

      if (signUpError) {
        if (isMounted.current) {
          setError(signUpError.message);
        }
      } else {
        if (isMounted.current) {
          setMessage(
            'Check your email to confirm your account, then sign in. If you do not see it, check your spam or junk folder.'
          );
        }
      }
    } else {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        if (isMounted.current) {
          setError(signInError.message);
        }
      } else {
        history.push(returnTo);
      }
    }

    if (isMounted.current) {
      setIsSending(false);
    }
  };

  return (
    <div className="login">
      <div className="login-toggle">
        <button
          className={mode === 'signin' ? 'active' : ''}
          onClick={() => setMode('signin')}
        >
          Sign In
        </button>
        <button
          className={mode === 'signup' ? 'active' : ''}
          onClick={() => setMode('signup')}
        >
          Sign Up
        </button>
      </div>
      <h2>{mode === 'signup' ? 'Create Account' : 'Welcome Back'}</h2>
      <p>
        {mode === 'signup'
          ? 'Create an account with your email and password.'
          : 'Sign in with your email and password.'}
      </p>
      {mode === 'signup' && (
        <p className="login-hint">
          After signing up, check your spam or junk folder for the validation
          email.
        </p>
      )}
      <form onSubmit={handleSubmit} className="login-form">
        <label>Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@example.com"
        />
        <label>Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Choose a strong password"
        />
        <button type="submit" disabled={isSending}>
          {isSending
            ? 'Working...'
            : mode === 'signup'
            ? 'Create account'
            : 'Sign in'}
        </button>
      </form>
      {message && <div className="login-message">{message}</div>}
      {error && <div className="login-error">{error}</div>}
    </div>
  );
};

export default Login;
